const PAST_CLS = { pending: 'bg-amber-50 text-amber-800', confirmed: 'bg-sky-50 text-sky-800', completed: 'bg-emerald-50 text-emerald-800',
  closed: 'bg-slate-200 text-slate-900 border border-slate-400', cancelled: 'bg-rose-50 text-rose-700', no_show: 'bg-slate-100 text-slate-600' };

// Earlier bookings for the phone number typed on the booking form.
export const PastBookings = ({ phone, excludeId, authHeader }) => {
  const [rows, setRows] = useState([]);
  const digits = String(phone || '').replace(/\D/g, '').slice(-10);
  useEffect(() => {
    if (digits.length !== 10) { setRows([]); return; }
    axios.get(`${API}/appointments/by-phone/${digits}`, { headers: authHeader(), params: { exclude: excludeId || '' } })
      .then((r) => setRows(r.data)).catch(() => setRows([]));
  }, [digits, excludeId]); // eslint-disable-line
  if (!rows.length) return null;
  return (
    <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid="apt-past-bookings">
      <div className="text-xs font-semibold text-slate-700 mb-1.5">Past bookings for this number ({rows.length})</div>
      <ul className="space-y-1 max-h-40 overflow-y-auto">
        {rows.map((a) => (
          <li key={a.id} className="flex items-center gap-2 text-xs" data-testid={`apt-past-${a.id}`}>
            <a href={`/admin/appointments/${a.id}`} target="_blank" rel="noopener noreferrer" className="font-mono text-teal-700 hover:underline">{a.ref_no}</a>
            <span className="text-slate-600">{a.slot_date} · {a.service_type}</span>
            <span className={`ml-auto rounded-full px-2 py-0.5 text-[10px] font-semibold capitalize ${PAST_CLS[a.status] || ''}`} title={a.closed_reason || undefined}>{String(a.status).replace('_', ' ')}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};


import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Loader2, Search, UserRound } from 'lucide-react';
import { API } from '../../../lib/api';
import { inputCls } from '../billing/shared';

// Customer Master search shared by Quick fill and the Full name box.
const useCustomerSearch = (authHeader, q, enabled) => {
  const [matches, setMatches] = useState(null);
  useEffect(() => {
    if (!enabled) return undefined;
    let live = true;
    const t = setTimeout(() => {
      axios.get(`${API}/appointments/lookup`, { headers: authHeader(), params: { q: q.trim().slice(0, 80) } })
        .then(({ data }) => { if (live) setMatches(data.matches || []); }).catch(() => { if (live) setMatches([]); });
    }, 250);
    return () => { live = false; clearTimeout(t); };
  }, [q, enabled, authHeader]);
  return matches;
};

const useOutside = (ref, onOut) => useEffect(() => {
  const close = (e) => { if (ref.current && !ref.current.contains(e.target)) onOut(); };
  document.addEventListener('pointerdown', close);
  return () => document.removeEventListener('pointerdown', close);
}, [ref, onOut]);

const MatchList = ({ matches, onPick, testid, emptyText }) => (
  <ul className="absolute z-30 mt-1 max-h-72 w-full overflow-y-auto rounded-lg border border-slate-200 bg-white shadow-lg" data-testid={`${testid}-results`}>
    {matches === null && <li className="flex items-center gap-2 px-3 py-2 text-xs text-slate-500"><Loader2 size={13} className="animate-spin" /> Loading customers…</li>}
    {matches?.length === 0 && <li className="px-3 py-2 text-xs text-slate-500" data-testid={`${testid}-none`}>{emptyText}</li>}
    {(matches || []).map((m, i) => (
      <li key={`${m.phone}-${m.name}-${m.vehicle.registration_number}`}>
        {/* pointerdown so the pick lands before the input blurs (iPad) */}
        <button type="button" onPointerDown={(e) => { e.preventDefault(); onPick(m); }}
          className="flex w-full items-center gap-3 px-3 py-2.5 text-left hover:bg-teal-50" data-testid={`${testid}-${i}`}>
          <UserRound size={15} className="shrink-0 text-slate-400" />
          <span className="min-w-0 flex-1">
            <span className="block truncate text-sm font-semibold text-slate-900">{[m.prefix, m.name].filter(Boolean).join(' ') || 'Unnamed'} {m.phone && <span className="font-mono text-xs font-normal text-slate-500">· {m.phone}</span>}</span>
            <span className="block truncate text-xs text-slate-500">{[m.vehicle.make, m.vehicle.model, m.vehicle.registration_number].filter(Boolean).join(' · ') || 'No vehicle on file'}</span>
          </span>
        </button>
      </li>
    ))}
  </ul>
);

// Turns off browser/iPad contact autofill on these boxes.
const NO_AUTOFILL = { autoComplete: 'off', autoCorrect: 'off', autoCapitalize: 'off', spellCheck: false, 'data-lpignore': 'true', 'data-form-type': 'other' };

// What a typed search looks like: a mobile number, a registration number, or a name.
const asNewCustomer = (text) => {
  const t = text.trim();
  const digits = t.replace(/\D/g, '');
  if (digits.length >= 10 && digits.length === t.replace(/[\s+()-]/g, '').length) return { phone: digits.slice(-10) };
  if (/^[A-Za-z]{2}\s?\d{1,2}\s?[A-Za-z]{0,3}\s?\d{3,4}$/.test(t)) return { registration_number: t.toUpperCase().replace(/[^A-Z0-9]/g, '') };
  return { name: t };
};

export const QuickFill = ({ authHeader, onPick, onNew }) => {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const box = useRef(null);
  const matches = useCustomerSearch(authHeader, q, open);
  const close = useCallback(() => setOpen(false), []);
  useOutside(box, close);
  return (
    <div ref={box} className="relative mb-3" data-testid="apt-quickfill">
      <label className="flex items-center gap-2 rounded-lg border border-teal-300 bg-teal-50/60 px-3">
        <Search size={15} className="text-teal-700" />
        <input type="search" name="apt-customer-search" className="w-full bg-transparent py-2 text-sm outline-none" placeholder="Pick existing customer: name, mobile or reg. no."
          value={q} onChange={(e) => { setQ(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)} {...NO_AUTOFILL} data-testid="apt-quickfill-input" />
      </label>
      {open && (matches?.length || q.trim().length < 2 || !onNew
        ? <MatchList matches={matches} testid="apt-quickfill" emptyText="No matching customer. Fill in the details below." onPick={(m) => { onPick(m); setOpen(false); setQ(''); }} />
        : matches && (
          <div className="absolute z-20 mt-1 w-full rounded-lg border border-slate-200 bg-white p-2 text-sm shadow-lg" data-testid="apt-quickfill-empty">
            <p className="px-1 pb-1.5 text-xs text-slate-500">No registered customer matches.</p>
            <button type="button" onClick={() => { onNew(asNewCustomer(q)); setOpen(false); setQ(''); }} className="w-full rounded-md bg-teal-600 px-3 py-2 text-left text-sm font-semibold text-white hover:bg-teal-700" data-testid="apt-quickfill-new">
              + New customer: “{q.trim()}”
            </button>
          </div>
        ))}
    </div>
  );
};

export const NameWithSuggest = ({ authHeader, value, onChange, onPick, className }) => {
  const [open, setOpen] = useState(false);
  const box = useRef(null);
  const typed = (value || '').trim().length > 0;
  const matches = useCustomerSearch(authHeader, value || '', open && typed);
  const close = useCallback(() => setOpen(false), []);
  useOutside(box, close);
  return (
    <div ref={box} className="relative">
      <input className={className} placeholder="Full name" name="apt-customer-name" value={value} {...NO_AUTOFILL}
        onChange={(e) => { onChange(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)} data-testid="apt-name" />
      {open && typed && matches?.length > 0 && <MatchList matches={matches} testid="apt-name-suggest" onPick={(m) => { onPick(m); setOpen(false); }} />}
    </div>
  );
};

export const PREFIXES = ['Mr', 'Mrs', 'Ms', 'Miss', 'Dr', 'M/s'];
export const withPrefix = (c) => [c?.prefix, c?.name].filter(Boolean).join(' ');

export const PrefixSelect = ({ value, onChange, className, testid }) => (
  <select className={className} value={value} onChange={(e) => onChange(e.target.value)} aria-label="Title" data-testid={testid}>
    <option value="">Title</option>
    {PREFIXES.map((p) => <option key={p} value={p}>{p}</option>)}
  </select>
);


export const PickupCard = ({ pickup, staff, onChange }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="apt-pickup-card">
    <label className="flex items-center justify-between gap-2">
      <span className="text-sm font-semibold text-slate-800">Pickup &amp; drop required</span>
      <input type="checkbox" className="h-4 w-4 accent-teal-600" checked={!!pickup.required} onChange={(e) => onChange({ required: e.target.checked })} data-testid="apt-pickup-toggle" />
    </label>
    {pickup.required && (
      <div className="mt-3 space-y-2">
        <textarea className={inputCls} rows={2} placeholder="Pickup address" value={pickup.address} onChange={(e) => onChange({ address: e.target.value })} data-testid="apt-pickup-address" />
        <div className="grid grid-cols-2 gap-2">
          <input type="time" className={inputCls} value={pickup.time} onChange={(e) => onChange({ time: e.target.value })} data-testid="apt-pickup-time" />
          <select className={inputCls} value={pickup.staff_id} data-testid="apt-pickup-staff"
            onChange={(e) => onChange({ staff_id: e.target.value, staff_name: staff.find((s) => s.id === e.target.value)?.name || '' })}>
            <option value="">Driver / staff</option>
            {staff.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>
      </div>
    )}
  </div>
);
